import { Link } from 'react-router-dom'
import { ROLE_LABELS, type Role } from '@crm/shared'
import { NAV_GROUPS, NAV_SECONDARY, leafVisible } from '../../shared/components/navConfig'
import { useAuthStore } from '../auth/authStore'
import { NewsFeedWidget } from '../news/NewsFeedWidget'
import { AdminDashboard } from './AdminDashboard'
import { AssistantDashboard } from './AssistantDashboard'
import { ManagerDashboard } from './ManagerDashboard'
import { ProcurementDashboard } from './ProcurementDashboard'
import { RosDashboard } from './RosDashboard'

export function DashboardPage() {
  const { user } = useAuthStore()
  // Роль-свитч: каждая роль получает свой дашборд — только по белому списку.
  if (user?.role === 'ADMIN') return <AdminDashboard />
  // Генеральный директор — дашборд как у админа (read-only обеспечивает бэк).
  if (user?.role === 'GENERAL_DIRECTOR') return <AdminDashboard />
  if (user?.role === 'MANAGER') return <ManagerDashboard />
  if (user?.role === 'PROCUREMENT') return <ProcurementDashboard />
  // ROP (Руководитель отдела продаж) видит тот же дашборд, что и админ, но
  // данные ограничены менеджерами его городов (скоуп считается на бэке).
  if (user?.role === 'ROP') return <AdminDashboard />
  // ROS (Руководитель отдела закупок) — дашборд РОЗ: агрегат квотирования по
  // команде закупщиков (этап 3, спека stages-3-5-plan.md §5).
  if (user?.role === 'ROS') return <RosDashboard />
  // ASSISTANT (Помощник) — рабочая сводка по заявкам прикреплённых менеджеров.
  if (user?.role === 'ASSISTANT') return <AssistantDashboard />
  // Роли этапа 3 (бухгалтер, логист, склад) и любая неизвестная роль — приветствие
  // со ссылками на открытые разделы и лента новостей (с 25.09.2026; других запросов
  // к API нет). Раньше сюда стоял LegacyDashboard: карточки-ссылки в продажи и
  // закупку и лента новостей (23.09.2026, docs/stage-3-plan.md, C1).
  return <StubDashboard name={user?.name ?? user?.email ?? ''} role={user?.role ?? ''} />
}

function StubDashboard({ name, role }: { name: string; role: string }) {
  const roleLabel = ROLE_LABELS[role as Role] ?? role
  // Разделы, уже открытые роли, — из того же меню (права выдаются кусками этапа 3).
  const sections = [...NAV_GROUPS.flatMap((g) => g.children), ...NAV_SECONDARY].filter((l) =>
    leafVisible(l, role),
  )
  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold text-gray-900">Главная</h1>
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <p className="text-sm text-gray-700">
          {name ? `${name}, добро пожаловать в micro·hub.` : 'Добро пожаловать в micro·hub.'}
        </p>
        {sections.length > 0 ? (
          <>
            <p className="mt-1 text-sm text-gray-500">
              {roleLabel ? `Роль «${roleLabel}». ` : ''}Доступные разделы:
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {sections.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="px-3 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700"
                >
                  {l.label}
                </Link>
              ))}
            </div>
            <p className="mt-3 text-xs text-gray-400">
              Остальные разделы для вашей роли появятся здесь, когда будут готовы.
            </p>
          </>
        ) : (
          <p className="mt-1 text-sm text-gray-500">
            {roleLabel ? `Роль «${roleLabel}». ` : ''}Разделы для вашей роли пока в разработке — они
            появятся здесь, когда будут готовы.
          </p>
        )}
      </div>
      <NewsFeedWidget />
    </div>
  )
}
